import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../../UserContext";
import "./styles.css";
import { Dropdown, Form } from "react-bootstrap";
import AddNew from "./Add New/index";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaSort,
  FaFileInvoice,
  FaPlus,
  FaTimes,
  FaFileAlt,
} from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";

const Invoices = () => {
  const [t, i18n] = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  // Core state
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [saleInvoices, setSaleInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // New state for enhanced functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // all | paid | partially_paid | not_paid
  const [sortOrder, setSortOrder] = useState("newest"); // newest | oldest
  const [activeTab, setActiveTab] = useState("purchase"); // purchase | sale

  // Form state
  const [currentView, setCurrentView] = useState("main");
  const [shouldRefetch, setShouldRefetch] = useState(false);

  // Infinite scroll state
  const [displayCount, setDisplayCount] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef();
  const lastInvoiceRef = useRef();

  // Handle navigation from BusinessEntityProfile
  const preSelectedEntity = location.state?.preSelectedEntity;
  const entityType = location.state?.entityType;
  const showAddForm = location.state?.showAddForm;
  const returnTo = location.state?.returnTo;

  // RTL support
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  useEffect(() => {
    // If coming from entity profile, show add form
    if (showAddForm && entityType) {
      setCurrentView(entityType === "supplier" ? "purchase" : "sale");
    }
  }, [showAddForm, entityType]);

  // Fetch purchase invoices
  useEffect(() => {
    if (currentView === "main") {
      // Only show loading on initial load or explicit refresh, not when returning from form
      if (!initialLoadComplete || shouldRefetch) {
        setLoading(true);
      }

      axios
        .get(
          `api/invoice/purchase/${userData.Tenant?.id}/${userData.Farm?.id}`,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        )
        .then((response) => {
          const invoices = response.data.items || [];
          setPurchaseInvoices(invoices);
          setLoading(false);
          setInitialLoadComplete(true);
        })
        .catch((error) => {
          console.error("Error fetching purchase invoices:", error);
          setLoading(false);
          setInitialLoadComplete(true);
        });
    }
  }, [token, userData, shouldRefetch, currentView]);

  // Fetch sale invoices
  useEffect(() => {
    if (currentView === "main") {
      axios
        .get(`api/invoice/sale/${userData.Tenant?.id}/${userData.Farm?.id}`, {
          headers: {
            Authorization: `${token}`,
          },
        })
        .then((response) => {
          const invoices = response.data.items || [];
          setSaleInvoices(invoices);
        })
        .catch((error) => {
          console.error("Error fetching sale invoices:", error);
        });
    }
  }, [token, userData, shouldRefetch, currentView]);

  // Helper function to determine invoice state
  const getInvoiceState = (invoice) => {
    const amountPaid = invoice.amountPaid || 0;
    const totalAmount = invoice.totalAmount || invoice.total || 0;

    if (amountPaid > 0 && amountPaid < totalAmount) {
      return "partially_paid";
    } else if (totalAmount <= amountPaid && totalAmount > 0) {
      return "paid";
    } else if (amountPaid === 0) {
      return "not_paid";
    }
    return "not_paid";
  };

  // Filter invoices by payment status
  const filterInvoicesByStatus = useCallback((invoices, filter) => {
    if (filter === "all") return invoices;

    return invoices.filter((invoice) => {
      const state = getInvoiceState(invoice);

      switch (filter) {
        case "paid":
          return state === "paid";
        case "partially_paid":
          return state === "partially_paid";
        case "not_paid":
          return state === "not_paid";
        default:
          return true;
      }
    });
  }, []);

  // Search invoices
  const searchInvoices = useCallback(
    (invoices, query) => {
      if (!query.trim()) return invoices;

      const queryLower = query.toLowerCase();

      return invoices.filter((invoice) => {
        // Search in multiple fields
        const searchFields = [
          invoice.number,
          invoice.invoiceNumber,
          invoice.supplier?.name,
          invoice.client?.name,
          invoice.supplier?.nameAr,
          invoice.client?.nameAr,
          invoice.totalAmount?.toString(),
        ].filter(Boolean);

        const searchText = searchFields.join(" ").toLowerCase();

        // Support Arabic number search
        let normalizedQuery = queryLower;
        if (isRTL) {
          const arabicNumbers = [
            "٠",
            "١",
            "٢",
            "٣",
            "٤",
            "٥",
            "٦",
            "٧",
            "٨",
            "٩",
          ];
          const englishNumbers = [
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9",
          ];
          arabicNumbers.forEach((ar, idx) => {
            normalizedQuery = normalizedQuery.replace(
              new RegExp(ar, "g"),
              englishNumbers[idx]
            );
          });
        }

        return searchText.includes(normalizedQuery);
      });
    },
    [isRTL]
  );

  // Sort invoices
  const sortInvoices = useCallback((invoices, order) => {
    const sorted = [...invoices];
    sorted.sort((a, b) => {
      const dateA = a.invoiceDate || a.date || a.createTs || 0;
      const dateB = b.invoiceDate || b.date || b.createTs || 0;

      return order === "newest" ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, []);

  // Get processed invoices based on current filters
  const getProcessedInvoices = useMemo(() => {
    const currentInvoices =
      activeTab === "purchase" ? purchaseInvoices : saleInvoices;

    // Apply filters in sequence
    let processed = [...currentInvoices];
    processed = filterInvoicesByStatus(processed, filterType);
    processed = searchInvoices(processed, searchQuery);
    processed = sortInvoices(processed, sortOrder);

    return processed;
  }, [
    activeTab,
    purchaseInvoices,
    saleInvoices,
    filterType,
    searchQuery,
    sortOrder,
    filterInvoicesByStatus,
    searchInvoices,
    sortInvoices,
  ]);

  // Get currently displayed invoices for infinite scroll
  const displayedInvoices = useMemo(() => {
    return getProcessedInvoices.slice(0, displayCount);
  }, [getProcessedInvoices, displayCount]);

  // Setup infinite scroll observer
  useEffect(() => {
    const currentObserver = observerRef.current;
    if (currentObserver) {
      currentObserver.disconnect();
    }

    const callback = (entries) => {
      if (
        entries[0].isIntersecting &&
        displayCount < getProcessedInvoices.length
      ) {
        setDisplayCount((prev) =>
          Math.min(prev + 20, getProcessedInvoices.length)
        );
      }
    };

    observerRef.current = new IntersectionObserver(callback);

    if (lastInvoiceRef.current) {
      observerRef.current.observe(lastInvoiceRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [displayCount, getProcessedInvoices.length]);

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(20);
  }, [filterType, searchQuery, sortOrder, activeTab]);

  // Format date based on language
  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    const locale = isRTL ? "ar-EG" : "en-GB";
    const options = { month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleDateString(locale, options);
  };

  // Format currency based on language
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";

    const formatted = new Intl.NumberFormat(isRTL ? "ar-EG" : "en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

    return `${formatted} ${t("l.e")}`;
  };

  // Navigate to invoice details
  const handleInvoiceClick = (invoice) => {
    navigate(`/invoices/${invoice.id}`, {
      state: {
        invoice,
        accountType: activeTab === "purchase" ? "supplier" : "client",
      },
    });
  };

  // Handle add new invoice
  const handleAddNewClick = (type) => {
    setCurrentView(type);
  };

  // Handle cancel from add form
  const handleCancel = () => {
    setCurrentView("main");
    // Don't trigger loading when canceling - data is still fresh
    // Only refetch if actually needed (e.g., after successful submission)
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
    setFilterType("all");
    setSortOrder("newest");
    setDisplayCount(20);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterType("all");
    setSortOrder("newest");
    setDisplayCount(20);
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || filterType !== "all";

  if (currentView === "purchase") {
    return (
      <AddNew
        onCancel={handleCancel}
        type="purchase"
        token={token}
        userData={userData}
        formatDate={formatDate}
        setShouldRefetch={setShouldRefetch}
        preSelectedEntity={preSelectedEntity}
        returnTo={returnTo}
      />
    );
  }

  if (currentView === "sale") {
    return (
      <AddNew
        onCancel={handleCancel}
        type="sale"
        token={token}
        userData={userData}
        formatDate={formatDate}
        setShouldRefetch={setShouldRefetch}
        preSelectedEntity={preSelectedEntity}
        returnTo={returnTo}
      />
    );
  }

  return (
    <div className="invoices-container">
      {/* Header Section */}
      <div className="invoices-header">
        <div className="header-left">
          <IoReceiptOutline className="header-icon" />
          <h1 className="header-title">{t("Invoices")}</h1>
        </div>
        <button
          className="add-invoice-btn"
          onClick={() => {
            // Open add form based on current active tab
            setCurrentView(activeTab === "purchase" ? "purchase" : "sale");
          }}
          title={
            activeTab === "purchase"
              ? t("Add Purchase Invoice")
              : t("Add Sale Invoice")
          }
        >
          <FaPlus className="btn-icon" />
          <span>{t("Add Invoice")}</span>
        </button>
      </div>

      {/* Tab Navigation with integrated stats */}
      <div className="tab-navigation">
        <button
          className={`tab-item ${activeTab === "purchase" ? "active" : ""}`}
          onClick={() => handleTabChange("purchase")}
        >
          <span className="tab-label">{t("Purchase Invoices")}</span>
          <span className="tab-badge">{purchaseInvoices.length}</span>
        </button>
        <button
          className={`tab-item ${activeTab === "sale" ? "active" : ""}`}
          onClick={() => handleTabChange("sale")}
        >
          <span className="tab-label">{t("Sale Invoices")}</span>
          <span className="tab-badge">{saleInvoices.length}</span>
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="invoices-search-box">
          <FaSearch className="invoices-search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder={t("Search invoices...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery("")}>
              <FaTimes />
            </button>
          )}
        </div>

        <div className="filter-group">
          <Dropdown className="filter-dropdown">
            <Dropdown.Toggle variant="light">
              <FaFilter className="filter-icon" />
              {filterType === "all"
                ? t("All")
                : filterType === "paid"
                ? t("Paid")
                : filterType === "partially_paid"
                ? t("Partially Paid")
                : t("Not Paid")}
            </Dropdown.Toggle>
            <Dropdown.Menu align={isRTL ? "end" : "start"}>
              <Dropdown.Item
                active={filterType === "all"}
                onClick={() => setFilterType("all")}
              >
                {t("All")}
              </Dropdown.Item>
              <Dropdown.Item
                active={filterType === "paid"}
                onClick={() => setFilterType("paid")}
              >
                {t("Paid")}
              </Dropdown.Item>
              <Dropdown.Item
                active={filterType === "partially_paid"}
                onClick={() => setFilterType("partially_paid")}
              >
                {t("Partially Paid")}
              </Dropdown.Item>
              <Dropdown.Item
                active={filterType === "not_paid"}
                onClick={() => setFilterType("not_paid")}
              >
                {t("Not Paid")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <button
            className={`sort-btn ${sortOrder}`}
            onClick={() =>
              setSortOrder(sortOrder === "newest" ? "oldest" : "newest")
            }
          >
            <FaSort />
            {sortOrder === "newest" ? t("Newest First") : t("Oldest First")}
          </button>
        </div>
      </div>

      {/* Results count */}
      {getProcessedInvoices.length > 0 && (
        <div className="results-info">
          <span className="results-text">
            {t("Showing")} <strong>{displayedInvoices.length}</strong> {t("of")}{" "}
            <strong>{getProcessedInvoices.length}</strong> {t("invoices")}
          </span>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={handleClearFilters}>
              {t("Clear filters")}
            </button>
          )}
        </div>
      )}

      {/* Invoice Cards Grid */}
      <div className="invoices-grid">
        {loading && !initialLoadComplete ? (
          // Skeleton Loading Cards - only show on initial load
          Array.from({ length: 6 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="invoice-card skeleton">
              <div className="card-header">
                <div className="skeleton-text skeleton-number"></div>
                <div className="skeleton-text skeleton-date"></div>
              </div>
              <div className="card-body">
                <div className="skeleton-text skeleton-name"></div>
                <div className="skeleton-text skeleton-amount"></div>
              </div>
              <div className="card-footer">
                <div className="footer-left">
                  <div className="skeleton-badge"></div>
                  <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-text skeleton-info"></div>
              </div>
            </div>
          ))
        ) : displayedInvoices.length > 0 ? (
          displayedInvoices.map((invoice, index) => {
            // Calculate payment status
            const amountPaid = invoice.amountPaid || 0;
            const totalAmount = invoice.totalAmount || invoice.total || 0;
            const paymentStatus = getInvoiceState(invoice);

            let statusText = t("Not Paid");
            if (paymentStatus === "partially_paid") {
              statusText = t("Partially Paid");
            } else if (paymentStatus === "paid") {
              statusText = t("Paid");
            }

            // Get business entity name
            const entityName =
              activeTab === "purchase"
                ? invoice.supplier?.name || invoice.supplier?.nameAr || "—"
                : invoice.client?.name || invoice.client?.nameAr || "—";

            const isLastItem = index === displayedInvoices.length - 1;

            return (
              <div
                key={invoice.id}
                ref={isLastItem ? lastInvoiceRef : null}
                className="invoice-card"
                onClick={() => handleInvoiceClick(invoice)}
              >
                {/* Card Header */}
                <div className="card-header">
                  <span className="invoice-number">
                    {invoice.number ||
                      invoice.invoiceNumber ||
                      `INV-${String(invoice.id).padStart(5, "0")}`}
                    #
                  </span>
                  <span className="invoice-date">
                    {formatDate(
                      invoice.invoiceDate || invoice.date || invoice.createTs
                    )}
                  </span>
                </div>

                {/* Card Body */}
                <div className="card-body">
                  <h3 className="entity-name">{entityName}</h3>
                  <div className="amount-container">
                    <span className="amount">
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="card-footer">
                  <div className="footer-left">
                    <span className={`status-badge ${paymentStatus}`}>
                      {statusText}
                    </span>
                    <span className={`type-badge ${activeTab}`}>
                      {activeTab === "purchase" ? t("Purchase") : t("Sale")}
                    </span>
                  </div>
                  {invoice.items && invoice.items.length > 0 && (
                    <span className="items-info">
                      {invoice.items.length}{" "}
                      {invoice.items.length === 1 ? t("item") : t("items")}
                      {invoice.discountRate > 0 &&
                        (invoice.discountType === "fixed" ||
                        invoice.discountType === "FIXED"
                          ? ` • ${formatCurrency(invoice.discountRate)} ${t(
                              "discount"
                            )}`
                          : ` • ${invoice.discountRate}% ${t("discount")}`)}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <FaFileInvoice className="empty-icon" />
            <h3>
              {searchQuery || filterType !== "all"
                ? t("No matching invoices")
                : t("No invoices found")}
            </h3>
            <p>
              {searchQuery || filterType !== "all"
                ? t("Try adjusting your search")
                : t("Click 'Add Invoice' to create your first invoice")}
            </p>
          </div>
        )}
      </div>

      {/* Loading more indicator */}
      {!loading && displayCount < getProcessedInvoices.length && (
        <div className="loading-more">
          <div className="loading-spinner small"></div>
          <span>{t("Loading more...")}</span>
        </div>
      )}
    </div>
  );
};

export default Invoices;
